import { useEffect, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";

interface TaxRate {
  id: string;
  code: string;
  name: string;
  rate: number;
}

export interface EditTaxRateDialogProps {
  taxRate: TaxRate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditTaxRateDialog({
  taxRate,
  open,
  onOpenChange,
  onUpdated,
}: EditTaxRateDialogProps) {
  const api = useDolfinClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (taxRate) {
      setCode(taxRate.code);
      setName(taxRate.name);
      setRate(String(taxRate.rate));
      setError(null);
    }
  }, [taxRate]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSubmitting(false);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!taxRate) return;
    setError(null);

    if (!code.trim()) {
      setError("Code is required.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const rateNum = Number(rate);
    if (isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      setError("Rate must be between 0 and 100.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PUT("/v1/tax-rates/{id}", {
      params: { path: { id: taxRate.id } },
      body: { code: code.trim(), name: name.trim(), rate: rateNum },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to update tax rate.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Tax Rate</DialogTitle>
            <DialogDescription>
              Update this tax rate.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-tax-rate-code">Code</Label>
              <Input
                id="edit-tax-rate-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tax-rate-name">Name</Label>
              <Input
                id="edit-tax-rate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tax-rate-rate">Rate (%)</Label>
              <Input
                id="edit-tax-rate-rate"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
